import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Route, useLocation } from "react-router-dom";
import { Loader } from "@monorepo/components";
import { LANGUAGE } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { lazyWithRetry, valueExists } from "@monorepo/helpers";
import { login } from "@monorepo/slices/auth";
import theme from "@monorepo/styles/theme";
import { LanguagesEnum } from "@monorepo/types";
import { Box } from "@mui/material";
import logoPL from "assets/img/easyBillLogo.png";
import logoLT from "assets/img/logoBig.png";
import { LoginContextProvider } from "context/LoginContext";

import { useAppDispatch } from "hooks";

const PhoneForm = lazyWithRetry(() => import("components/PhoneNumberForm/PhoneNumberForm"));
const ResetPasswordEnterPhoneForm = lazyWithRetry(
  () => import("components/ResetPasswordForms/ResetPasswordEnterPhoneForm/ResetPasswordEnterPhoneForm")
);
const ResetPasswordSubmitForm = lazyWithRetry(
  () => import("components/ResetPasswordForms/ResetPasswordSubmitForm/ResetPasswordSubmitForm")
);
const PasswordForm = lazyWithRetry(() => import("components/Password/Password"));
const SmsVerification = lazyWithRetry(() => import("components/VerifySMSCode/VerifyLoginSMSCode"));
const CreatePassword = lazyWithRetry(() => import("components/CreatePassword/CreateLoginPassword"));

interface IProps {
  path: string;
}

const SEARCH_PARAMS_KEYS = {
  TOKEN: "token",
  REMOTE_USER_ID: "remote_user_id",
};

export const Login = ({ path }: IProps) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const token = searchParams.get(SEARCH_PARAMS_KEYS.TOKEN);
  const remoteUserId = searchParams.get(SEARCH_PARAMS_KEYS.REMOTE_USER_ID);
  const dispatch = useAppDispatch();

  if (valueExists(token) && valueExists(remoteUserId)) {
    dispatch(
      login({
        access_token: token,
        token_type: "Bearer",
        remote_user_id: remoteUserId,
      })
    );
  }

  return (
    <LoginContextProvider>
      <Box
        sx={{
          display: "grid",
          gridRowGap: theme.spacing(4),
          gridTemplateColumns: "minmax(250px, 400px)",
          justifyContent: "center",
          alignContent: "flex-start",
          textAlign: "center",
          justifyItems: "center",
          height: "100%",
          margin: `0px ${theme.spacing(18 / 8)}`,
          overflowY: "auto",
          [theme.breakpoints.up("md")]: {
            alignContent: "center",
          },
          ...(path === routeConfig.login.resetPasswordSubmit && {
            [theme.breakpoints.up("sm")]: {
              alignContent: "start",
            },
          }),
        }}
      >
        <Box
          component="img"
          sx={{
            width: "75%",
            marginTop: "64px",
            justifySelf: "center",
          }}
          src={LANGUAGE === LanguagesEnum.PL ? logoPL : logoLT}
          alt={t("common.perlasgo_logo_alt")}
        />
        <Suspense fallback={<Loader />}>
          <Route exact path={routeConfig.login.password}>
            <PasswordForm />
          </Route>
          <Route exact path={routeConfig.login.createPassword}>
            <CreatePassword />
          </Route>
          <Route exact path={routeConfig.login.smsVerification}>
            <SmsVerification />
          </Route>
          <Route exact path={routeConfig.login.resetPasswordEnterPhone}>
            <ResetPasswordEnterPhoneForm />
          </Route>
          <Route exact path={routeConfig.login.resetPasswordSubmit}>
            <ResetPasswordSubmitForm />
          </Route>
          <Route exact path={[routeConfig.login.self, routeConfig.home]}>
            <PhoneForm />
          </Route>
        </Suspense>
      </Box>
    </LoginContextProvider>
  );
};
